import React from 'react';
import { Typography, Box, Grid, Paper, Divider, CircularProgress } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import HomeIcon from '@mui/icons-material/Home';
import ApartmentIcon from '@mui/icons-material/Apartment';
import BusinessIcon from '@mui/icons-material/Business';
import { currencyParser } from '../../../helper/parser';
import SalesRatioProgressBar from './SalesRatioProgressBar';

const useStyles = makeStyles((theme) => ({
	paper: {
		padding: theme.spacing(4),
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		width: '100%',
		marginBottom: theme.spacing(3)
	},
	root: {
		padding: theme.spacing(3),
		width: '100%'
	},
	title: {
		fontWeight: 'bold',
		marginBottom: theme.spacing(3),
		color: '#333',
		textAlign: 'center'
	},
	subtitle: {
		color: '#666',
		marginBottom: theme.spacing(4),
		textAlign: 'center'
	},
	propertyTypeSection: {
		textAlign: 'center',
		marginBottom: theme.spacing(4)
	},
	propertyTypeTitle: {
		fontWeight: 'bold',
		marginBottom: theme.spacing(2),
		color: '#004F2B'
	},
	statsGrid: {
		marginBottom: theme.spacing(2)
	},
	statItem: {
		marginBottom: theme.spacing(1)
	},
	statLabel: {
		color: '#666',
		fontSize: '0.9rem'
	},
	statValue: {
		fontWeight: 'bold',
		fontSize: '1.1rem',
		color: '#333'
	},
	icon: {
		fontSize: 40,
		color: '#004F2B',
		marginBottom: theme.spacing(1)
	},
	cityStats: {
		marginTop: theme.spacing(4),
		padding: theme.spacing(2),
		backgroundColor: '#f5f5f5',
		borderRadius: theme.spacing(1)
	},
	ratioGrid: {
		textAlign: 'center'
	},
	ratioValue: {
		fontWeight: 'bold',
		color: '#004F2B',
		fontSize: '1.2rem'
	},
	noDataMessage: {
		textAlign: 'center',
		marginTop: theme.spacing(2),
		color: '#666'
	}
}));

export default function LeadCityMarketUpdate({ listing }) {
	const classes = useStyles();

	const benchmarkPriceGVRpercityAllMonths = {
		'February 2025': {
			detached: {
				'Lower Mainland': {
					benchmarkPrice: 1792400
				},
				'Greater Vancouver': {
					benchmarkPrice: 2006100
				},
				'Bowen Island': {
					benchmarkPrice: 1507600
				},
				'Burnaby East': {
					benchmarkPrice: 1959700
				},
				'Burnaby North': {
					benchmarkPrice: 2085400
				},
				'Burnaby South': {
					benchmarkPrice: 2207300
				},
				Coquitlam: {
					benchmarkPrice: 1796600
				},
				Ladner: {
					benchmarkPrice: 1398600
				},
				'Maple Ridge': {
					benchmarkPrice: 1250400
				},
				'New Westminster': {
					benchmarkPrice: 1574200
				},
				'North Vancouver': {
					benchmarkPrice: 2232600
				},
				'Pitt Meadows': {
					benchmarkPrice: 1179600
				},
				'Port Coquitlam': {
					benchmarkPrice: 1409400
				},
				'Port Moody': {
					benchmarkPrice: 2089700
				},
				Richmond: {
					benchmarkPrice: 2139000
				},
				Squamish: {
					benchmarkPrice: 1464000
				},
				'Sunshine Coast': {
					benchmarkPrice: 927700
				},
				Tsawwassen: {
					benchmarkPrice: 1654800
				},
				'Vancouver East': {
					benchmarkPrice: 1862500
				},
				'Vancouver West': {
					benchmarkPrice: 3493200
				},
				'West Vancouver': {
					benchmarkPrice: 3127700
				},
				Whistler: {
					benchmarkPrice: 2159300
				}
			},
			attached: {
				'Lower Mainland': {
					benchmarkPrice: 959200
				},
				'Greater Vancouver': {
					benchmarkPrice: 1087100
				},
				'Burnaby East': {
					benchmarkPrice: 915800
				},
				'Burnaby North': {
					benchmarkPrice: 911000
				},
				'Burnaby South': {
					benchmarkPrice: 1108700
				},
				Coquitlam: {
					benchmarkPrice: 1056100
				},
				Ladner: {
					benchmarkPrice: 1041400
				},
				'Maple Ridge': {
					benchmarkPrice: 771100
				},
				'New Westminster': {
					benchmarkPrice: 950700
				},
				'North Vancouver': {
					benchmarkPrice: 1366100
				},
				'Pitt Meadows': {
					benchmarkPrice: 836400
				},
				'Port Coquitlam': {
					benchmarkPrice: 956300
				},
				'Port Moody': {
					benchmarkPrice: 1011300
				},
				Richmond: {
					benchmarkPrice: 1109300
				},
				Squamish: {
					benchmarkPrice: 863200
				},
				'Sunshine Coast': {
					benchmarkPrice: 629200
				},
				Tsawwassen: {
					benchmarkPrice: 1007800
				},
				'Vancouver East': {
					benchmarkPrice: 1169800
				},
				'Vancouver West': {
					benchmarkPrice: 1414300
				},
				Whistler: {
					benchmarkPrice: 1384800
				}
			},
			apartment: {
				'Lower Mainland': {
					benchmarkPrice: 695000
				},
				'Greater Vancouver': {
					benchmarkPrice: 747500
				},
				'Burnaby East': {
					benchmarkPrice: 784900
				},
				'Burnaby North': {
					benchmarkPrice: 732000
				},
				'Burnaby South': {
					benchmarkPrice: 823100
				},
				Coquitlam: {
					benchmarkPrice: 732000
				},
				Ladner: {
					benchmarkPrice: 624500
				},
				'Maple Ridge': {
					benchmarkPrice: 522500
				},
				'New Westminster': {
					benchmarkPrice: 657600
				},
				'North Vancouver': {
					benchmarkPrice: 804100
				},
				'Pitt Meadows': {
					benchmarkPrice: 599800
				},
				'Port Coquitlam': {
					benchmarkPrice: 630700
				},
				'Port Moody': {
					benchmarkPrice: 718000
				},
				Richmond: {
					benchmarkPrice: 733800
				},
				Squamish: {
					benchmarkPrice: 544000
				},
				'Sunshine Coast': {
					benchmarkPrice: 447000
				},
				Tsawwassen: {
					benchmarkPrice: 619200
				},
				'Vancouver East': {
					benchmarkPrice: 684800
				},
				'Vancouver West': {
					benchmarkPrice: 815700
				},
				'West Vancouver': {
					benchmarkPrice: 1215800
				},
				Whistler: {
					benchmarkPrice: 588000
				}
			}
		}
	};
	const marketDataGVRpercityAllMonths = {
		'February 2025': {
			detached: {
				'Bowen Island': {
					listings: 6,
					sales: 7,
					salesToListings: '117%',
					median_selling_price: 'n/a'
				},
				Burnaby: {
					listings: 112,
					sales: 35,
					salesToListings: '31%',
					median_selling_price: '$1,950,000'
				},
				Coquitlam: {
					listings: 112,
					sales: 40,
					salesToListings: '36%',
					median_selling_price: '$1,722,500'
				},
				'Delta - South': {
					listings: 73,
					sales: 17,
					salesToListings: '23%',
					median_selling_price: 'n/a'
				},
				'Islands - Gulf': {
					listings: 11,
					sales: 4,
					salesToListings: '36%',
					median_selling_price: 'n/a'
				},
				'Maple Ridge/Pitt Meadows': {
					listings: 181,
					sales: 63,
					salesToListings: '35%',
					median_selling_price: '$1,400,000'
				},
				'New Westminster': {
					listings: 26,
					sales: 11,
					salesToListings: '42%',
					median_selling_price: 'n/a'
				},
				'North Vancouver': {
					listings: 108,
					sales: 48,
					salesToListings: '44%',
					median_selling_price: '$2,262,751'
				},
				'Port Coquitlam': {
					listings: 43,
					sales: 18,
					salesToListings: '42%',
					median_selling_price: 'n/a'
				},
				'Port Moody/Belcarra': {
					listings: 31,
					sales: 10,
					salesToListings: '32%',
					median_selling_price: 'n/a'
				},
				Richmond: {
					listings: 153,
					sales: 41,
					salesToListings: '27%',
					median_selling_price: '$2,288,000'
				},
				Squamish: {
					listings: 24,
					sales: 18,
					salesToListings: '75%',
					median_selling_price: 'n/a'
				},
				'Sunshine Coast': {
					listings: 74,
					sales: 39,
					salesToListings: '53%',
					median_selling_price: '$875,000'
				},
				'Vancouver East': {
					listings: 171,
					sales: 54,
					salesToListings: '32%',
					median_selling_price: '$1,900,000'
				},
				'Vancouver West': {
					listings: 172,
					sales: 36,
					salesToListings: '21%',
					median_selling_price: '$3,550,000'
				},
				'West Vancouver/Howe Sound': {
					listings: 126,
					sales: 28,
					salesToListings: '22%',
					median_selling_price: '$3,375,000'
				},
				'Whistler/Pemberton': {
					listings: 21,
					sales: 8,
					salesToListings: '38%',
					median_selling_price: 'n/a'
				}
			},
			attached: {
				'Bowen Island': {
					listings: 0,
					sales: 0,
					salesToListings: 'n/a',
					median_selling_price: 'n/a'
				},
				Burnaby: {
					listings: 88,
					sales: 34,
					salesToListings: '39%',
					median_selling_price: '$1,099,500'
				},
				Coquitlam: {
					listings: 92,
					sales: 33,
					salesToListings: '36%',
					median_selling_price: '$1,249,900'
				},
				'Delta - South': {
					listings: 39,
					sales: 17,
					salesToListings: '44%',
					median_selling_price: 'n/a'
				},
				'Islands - Gulf': {
					listings: 0,
					sales: 0,
					salesToListings: 'n/a',
					median_selling_price: 'n/a'
				},
				'Maple Ridge/Pitt Meadows': {
					listings: 72,
					sales: 43,
					salesToListings: '60%',
					median_selling_price: '$822,000'
				},
				'New Westminster': {
					listings: 19,
					sales: 9,
					salesToListings: '47%',
					median_selling_price: 'n/a'
				},
				'North Vancouver': {
					listings: 67,
					sales: 27,
					salesToListings: '40%',
					median_selling_price: '$1,335,000'
				},
				'Port Coquitlam': {
					listings: 32,
					sales: 12,
					salesToListings: '38%',
					median_selling_price: 'n/a'
				},
				'Port Moody/Belcarra': {
					listings: 19,
					sales: 10,
					salesToListings: '53%',
					median_selling_price: 'n/a'
				},
				Richmond: {
					listings: 125,
					sales: 39,
					salesToListings: '31%',
					median_selling_price: '$1,038,000'
				},
				Squamish: {
					listings: 30,
					sales: 23,
					salesToListings: '77%',
					median_selling_price: '$1,100,000'
				},
				'Sunshine Coast': {
					listings: 10,
					sales: 5,
					salesToListings: '50%',
					median_selling_price: 'n/a'
				},
				'Vancouver East': {
					listings: 148,
					sales: 58,
					salesToListings: '39%',
					median_selling_price: '$1,358,500'
				},
				'Vancouver West': {
					listings: 141,
					sales: 35,
					salesToListings: '25%',
					median_selling_price: '$1,525,000'
				},
				'West Vancouver/Howe Sound': {
					listings: 17,
					sales: 4,
					salesToListings: '24%',
					median_selling_price: 'n/a'
				},
				'Whistler/Pemberton': {
					listings: 28,
					sales: 10,
					salesToListings: '36%',
					median_selling_price: 'n/a'
				}
			},
			apartment: {
				'Bowen Island': {
					listings: 0,
					sales: 0,
					salesToListings: 'n/a',
					median_selling_price: 'n/a'
				},
				Burnaby: {
					listings: 431,
					sales: 155,
					salesToListings: '36%',
					median_selling_price: '$740,000'
				},
				Coquitlam: {
					listings: 263,
					sales: 88,
					salesToListings: '33%',
					median_selling_price: '$685,000'
				},
				'Delta - South': {
					listings: 51,
					sales: 22,
					salesToListings: '43%',
					median_selling_price: '$732,500'
				},
				'Islands - Gulf': {
					listings: 0,
					sales: 2,
					salesToListings: 'n/a',
					median_selling_price: 'n/a'
				},
				'Maple Ridge/Pitt Meadows': {
					listings: 88,
					sales: 43,
					salesToListings: '49%',
					median_selling_price: '$533,476'
				},
				'New Westminster': {
					listings: 161,
					sales: 68,
					salesToListings: '42%',
					median_selling_price: '$580,000'
				},
				'North Vancouver': {
					listings: 172,
					sales: 78,
					salesToListings: '45%',
					median_selling_price: '$779,500'
				},
				'Port Coquitlam': {
					listings: 52,
					sales: 28,
					salesToListings: '54%',
					median_selling_price: '$580,000'
				},
				'Port Moody/Belcarra': {
					listings: 62,
					sales: 19,
					salesToListings: '31%',
					median_selling_price: 'n/a'
				},
				Richmond: {
					listings: 317,
					sales: 98,
					salesToListings: '31%',
					median_selling_price: '$664,000'
				},
				Squamish: {
					listings: 31,
					sales: 10,
					salesToListings: '32%',
					median_selling_price: 'n/a'
				},
				'Sunshine Coast': {
					listings: 7,
					sales: 3,
					salesToListings: '43%',
					median_selling_price: 'n/a'
				},
				'Vancouver East': {
					listings: 238,
					sales: 92,
					salesToListings: '39%',
					median_selling_price: '$702,500'
				},
				'Vancouver West': {
					listings: 751,
					sales: 236,
					salesToListings: '31%',
					median_selling_price: '$817,500'
				},
				'West Vancouver/Howe Sound': {
					listings: 29,
					sales: 7,
					salesToListings: '24%',
					median_selling_price: 'n/a'
				},
				'Whistler/Pemberton': {
					listings: 31,
					sales: 27,
					salesToListings: '87%',
					median_selling_price: '$635,000'
				}
			}
		}
	};

	const marketDataFVRBpercityAllMonths = {
		'February 2025': {
			detached: {
				Abbotsford: {
					sales: 48,
					newListings: 160,
					activeListings: 384,
					benchmarkPrice: 1204100,
					medianPrice: 1267500,
					averagePrice: 1243470
				},
				Mission: {
					sales: 18,
					newListings: 63,
					activeListings: 220,
					benchmarkPrice: 1056200,
					medianPrice: 1039500,
					averagePrice: 1062632
				},
				'South Surrey White Rock': {
					sales: 39,
					newListings: 163,
					activeListings: 454,
					benchmarkPrice: 1844400,
					medianPrice: 1900000,
					averagePrice: 2117166
				},
				Langley: {
					sales: 57,
					newListings: 194,
					activeListings: 400,
					benchmarkPrice: 1617700,
					medianPrice: 1450000,
					averagePrice: 1615749
				},
				'N. Delta': {
					sales: 16,
					newListings: 85,
					activeListings: 181,
					benchmarkPrice: 1398800,
					medianPrice: 1385000,
					averagePrice: 1475437
				},
				Surrey: {
					sales: 124,
					benchmarkPrice: 1622200,
					averagePrice: 1698154
				},
				'Surrey - Central': {
					sales: 46,
					newListings: 213,
					activeListings: 598,
					benchmarkPrice: 1520800,
					medianPrice: 1545500,
					averagePrice: 1610978
				},
				Cloverdale: {
					sales: 28,
					newListings: 75,
					activeListings: 150,
					benchmarkPrice: 1529200,
					medianPrice: 1359900,
					averagePrice: 1439764
				},
				'North Surrey': {
					sales: 18,
					newListings: 134,
					activeListings: 369,
					benchmarkPrice: 1524700,
					medianPrice: 1390000,
					averagePrice: 1565944
				}
			},
			attached: {
				Abbotsford: {
					sales: 36,
					newListings: 77,
					activeListings: 165,
					benchmarkPrice: 657600,
					medianPrice: 702500,
					averagePrice: 700266
				},
				Mission: {
					sales: 3,
					newListings: 10,
					activeListings: 36,
					benchmarkPrice: 667500,
					medianPrice: 799000,
					averagePrice: 777966
				},
				'South Surrey White Rock': {
					sales: 52,
					newListings: 121,
					activeListings: 219,
					benchmarkPrice: 927500,
					medianPrice: 975000,
					averagePrice: 1017718
				},
				Langley: {
					sales: 65,
					newListings: 145,
					activeListings: 234,
					benchmarkPrice: 864500,
					medianPrice: 870000,
					averagePrice: 882386
				},
				'N. Delta': {
					sales: 6,
					newListings: 20,
					activeListings: 39,
					benchmarkPrice: 899600,
					medianPrice: 920000,
					averagePrice: 917833
				},
				'City of Surrey - Combined*': {
					sales: 145,
					benchmarkPrice: 842700,
					averagePrice: 889712
				},
				'Surrey - Central': {
					sales: 54,
					newListings: 158,
					activeListings: 388,
					benchmarkPrice: 817600,
					medianPrice: 837500,
					averagePrice: 835103
				},
				Cloverdale: {
					sales: 24,
					newListings: 54,
					activeListings: 94,
					benchmarkPrice: 826500,
					medianPrice: 796500,
					averagePrice: 802804
				},
				'North Surrey': {
					sales: 15,
					newListings: 49,
					activeListings: 111,
					benchmarkPrice: 749000,
					medianPrice: 770000,
					averagePrice: 781606
				}
			},
			apartment: {
				Abbotsford: {
					sales: 48,
					newListings: 118,
					activeListings: 247,
					benchmarkPrice: 440100,
					medianPrice: 435000,
					averagePrice: 440877
				},
				Mission: {
					sales: 2,
					newListings: 29,
					activeListings: 51,
					benchmarkPrice: 453200,
					medianPrice: 448000,
					averagePrice: 448000
				},
				'South Surrey White Rock': {
					sales: 53,
					newListings: 175,
					activeListings: 365,
					benchmarkPrice: 609400,
					medianPrice: 615000,
					averagePrice: 757822
				},
				Langley: {
					sales: 78,
					newListings: 217,
					activeListings: 403,
					benchmarkPrice: 598500,
					medianPrice: 555000,
					averagePrice: 568720
				},
				'N. Delta': {
					sales: 5,
					newListings: 23,
					activeListings: 52,
					benchmarkPrice: 550500,
					medianPrice: 595000,
					averagePrice: 630000
				},
				'City of Surrey - Combined*': {
					sales: 126,
					benchmarkPrice: 525700,
					averagePrice: 575807
				},
				'Surrey - Central': {
					sales: 25,
					newListings: 83,
					activeListings: 212,
					benchmarkPrice: 546300,
					medianPrice: 555000,
					averagePrice: 558210
				},
				Cloverdale: {
					sales: 10,
					newListings: 42,
					activeListings: 60,
					benchmarkPrice: 582700,
					medianPrice: 547450,
					averagePrice: 627190
				},
				'North Surrey': {
					sales: 60,
					newListings: 270,
					activeListings: 723,
					benchmarkPrice: 481700,
					medianPrice: 477000,
					averagePrice: 490698
				}
			}
		},
		'March 2025': {
			detached: {
				Abbotsford: {
					sales: 51,
					newListings: 181,
					activeListings: 451,
					benchmarkPrice: 1225500,
					medianPrice: 1170000,
					averagePrice: 1238684
				},
				Mission: {
					sales: 31,
					newListings: 96,
					activeListings: 244,
					benchmarkPrice: 1064400,
					medianPrice: 1050000,
					averagePrice: 1075852
				},
				'White Rock / South Surrey': {
					sales: 41,
					newListings: 219,
					activeListings: 549,
					benchmarkPrice: 1904300,
					medianPrice: 1830888,
					averagePrice: 2007258
				},
				Langley: {
					sales: 79,
					newListings: 233,
					activeListings: 480,
					benchmarkPrice: 1635200,
					medianPrice: 1600000,
					averagePrice: 1731280
				},
				'Delta - North': {
					sales: 20,
					newListings: 81,
					activeListings: 199,
					benchmarkPrice: 1393000,
					medianPrice: 1392500,
					averagePrice: 1408950
				},
				'City of Surrey - Combined*': {
					sales: 135,
					benchmarkPrice: 1634900,
					averagePrice: 1674065
				},
				'Surrey - Central': {
					sales: 49,
					newListings: 269,
					activeListings: 695,
					benchmarkPrice: 1536900,
					medianPrice: 1470000,
					averagePrice: 1619357
				},
				'Surrey - Cloverdale': {
					sales: 26,
					newListings: 99,
					activeListings: 178,
					benchmarkPrice: 1525400,
					medianPrice: 1490500,
					averagePrice: 1482057
				},
				'Surrey - North': {
					sales: 24,
					newListings: 172,
					activeListings: 445,
					benchmarkPrice: 1506400,
					medianPrice: 1392500,
					averagePrice: 1514887
				}
			},
			townhouse: {
				Abbotsford: {
					sales: 38,
					newListings: 114,
					activeListings: 202,
					benchmarkPrice: 656100,
					medianPrice: 651000,
					averagePrice: 682911
				},
				Mission: {
					sales: 6,
					newListings: 17,
					activeListings: 42,
					benchmarkPrice: 668500,
					medianPrice: 709900,
					averagePrice: 670283
				},
				'White Rock / South Surrey': {
					sales: 53,
					newListings: 133,
					activeListings: 252,
					benchmarkPrice: 946400,
					medianPrice: 1040000,
					averagePrice: 1037864
				},
				Langley: {
					sales: 72,
					newListings: 197,
					activeListings: 282,
					benchmarkPrice: 865100,
					medianPrice: 862500,
					averagePrice: 869465
				},
				'Delta - North': {
					sales: 6,
					newListings: 15,
					activeListings: 36,
					benchmarkPrice: 956300,
					medianPrice: 947500,
					averagePrice: 983333
				},
				'City of Surrey - Combined*': {
					sales: 159,
					benchmarkPrice: 862500,
					averagePrice: 890275
				},
				'Surrey - Central': {
					sales: 63,
					newListings: 202,
					activeListings: 441,
					benchmarkPrice: 843600,
					medianPrice: 834900,
					averagePrice: 821065
				},
				'Surrey - Cloverdale': {
					sales: 25,
					newListings: 65,
					activeListings: 113,
					benchmarkPrice: 842100,
					medianPrice: 842500,
					averagePrice: 832876
				},
				'Surrey - North': {
					sales: 18,
					newListings: 58,
					activeListings: 127,
					benchmarkPrice: 752400,
					medianPrice: 786000,
					averagePrice: 777666
				}
			},
			apartment: {
				Abbotsford: {
					sales: 41,
					newListings: 158,
					activeListings: 307,
					benchmarkPrice: 443700,
					medianPrice: 420000,
					averagePrice: 460887
				},
				Mission: {
					sales: 4,
					newListings: 22,
					activeListings: 43,
					benchmarkPrice: 455800,
					medianPrice: 534000,
					averagePrice: 543225
				},
				'White Rock / South Surrey': {
					sales: 53,
					newListings: 185,
					activeListings: 423,
					benchmarkPrice: 626000,
					medianPrice: 612000,
					averagePrice: 683894
				},
				Langley: {
					sales: 104,
					newListings: 236,
					activeListings: 427,
					benchmarkPrice: 611900,
					medianPrice: 557500,
					averagePrice: 579845
				},
				'Delta - North': {
					sales: 8,
					newListings: 18,
					activeListings: 53,
					benchmarkPrice: 554500,
					medianPrice: 528500,
					averagePrice: 547050
				},
				'City of Surrey - Combined*': {
					sales: 152,
					benchmarkPrice: 528500,
					averagePrice: 529951
				},
				'Surrey - Central': {
					sales: 28,
					newListings: 83,
					activeListings: 214,
					benchmarkPrice: 552600,
					medianPrice: 553500,
					averagePrice: 540232
				},
				'Surrey - Cloverdale': {
					sales: 15,
					newListings: 42,
					activeListings: 78,
					benchmarkPrice: 592700,
					medianPrice: 490000,
					averagePrice: 494033
				},
				'Surrey - North': {
					sales: 78,
					newListings: 327,
					activeListings: 823,
					benchmarkPrice: 479100,
					medianPrice: 465000,
					averagePrice: 486974
				}
			}
		},
		'April 2025': {
			detached: {
				Abbotsford: {
					sales: 52,
					newListings: 183,
					activeListings: 485,
					benchmarkPrice: 1211600,
					medianPrice: 1207500,
					averagePrice: 1259028
				},
				Mission: {
					sales: 26,
					newListings: 88,
					activeListings: 268,
					benchmarkPrice: 1062100,
					medianPrice: 932500,
					averagePrice: 990888
				},
				'South Surrey White Rock': {
					sales: 52,
					newListings: 233,
					activeListings: 614,
					benchmarkPrice: 1910800,
					medianPrice: 1892500,
					averagePrice: 2013015
				},
				Langley: {
					sales: 67,
					newListings: 236,
					activeListings: 543,
					benchmarkPrice: 1650700,
					medianPrice: 1586000,
					averagePrice: 1636513
				},
				'N. Delta': {
					sales: 16,
					newListings: 95,
					activeListings: 228,
					benchmarkPrice: 1404100,
					medianPrice: 1337450,
					averagePrice: 1372743
				},
				'Surrey - combined': {
					sales: 151,
					benchmarkPrice: 1627800,
					averagePrice: 1694779
				},
				Surrey: {
					sales: 45,
					newListings: 281,
					activeListings: 778,
					benchmarkPrice: 1533200,
					medianPrice: 1462000,
					averagePrice: 1553077
				},
				Cloverdale: {
					sales: 31,
					newListings: 115,
					activeListings: 216,
					benchmarkPrice: 1527500,
					medianPrice: 1499999,
					averagePrice: 1515935
				},
				'Surrey - North': {
					sales: 27,
					newListings: 145,
					activeListings: 480,
					benchmarkPrice: 1483100,
					medianPrice: 1325000,
					averagePrice: 1562684
				}
			},
			townhouse: {
				Abbotsford: {
					sales: 32,
					newListings: 103,
					activeListings: 245,
					benchmarkPrice: 655700,
					medianPrice: 656450,
					averagePrice: 667284
				},
				Mission: {
					sales: 9,
					newListings: 14,
					activeListings: 42,
					benchmarkPrice: 662200,
					medianPrice: 642500,
					averagePrice: 653268
				},
				'South Surrey White Rock': {
					sales: 42,
					newListings: 139,
					activeListings: 290,
					benchmarkPrice: 954400,
					medianPrice: 970428,
					averagePrice: 1061994
				},
				Langley: {
					sales: 76,
					newListings: 186,
					activeListings: 310,
					benchmarkPrice: 861800,
					medianPrice: 852500,
					averagePrice: 887042
				},
				'N. Delta': {
					sales: 4,
					newListings: 19,
					activeListings: 37,
					benchmarkPrice: 961200,
					medianPrice: 912000,
					averagePrice: 880500
				},
				'City of Surrey - Combined*': {
					sales: 151,
					benchmarkPrice: 862900,
					averagePrice: 880123
				},
				'Surrey - Central': {
					sales: 60,
					newListings: 227,
					activeListings: 496,
					benchmarkPrice: 836100,
					medianPrice: 829900,
					averagePrice: 817859
				},
				Cloverdale: {
					sales: 32,
					newListings: 80,
					activeListings: 129,
					benchmarkPrice: 845900,
					medianPrice: 820000,
					averagePrice: 818668
				},
				'Surrey - North': {
					sales: 17,
					newListings: 40,
					activeListings: 127,
					benchmarkPrice: 758400,
					medianPrice: 755000,
					averagePrice: 766229
				}
			},
			apartment: {
				Abbotsford: {
					sales: 44,
					newListings: 147,
					activeListings: 346,
					benchmarkPrice: 438000,
					medianPrice: 416000,
					averagePrice: 445863
				},
				Mission: {
					sales: 4,
					newListings: 22,
					activeListings: 49,
					benchmarkPrice: 448300,
					medianPrice: 447500,
					averagePrice: 443250
				},
				'South Surrey White Rock': {
					sales: 72,
					newListings: 172,
					activeListings: 426,
					benchmarkPrice: 619400,
					medianPrice: 596450,
					averagePrice: 673674
				},
				Langley: {
					sales: 76,
					newListings: 219,
					activeListings: 475,
					benchmarkPrice: 611200,
					medianPrice: 580000,
					averagePrice: 589148
				},
				'N. Delta': {
					sales: 8,
					newListings: 21,
					activeListings: 54,
					benchmarkPrice: 563500,
					medianPrice: 620000,
					averagePrice: 589249
				},
				'City of Surrey - Combined*': {
					sales: 184,
					benchmarkPrice: 524000,
					averagePrice: 526911
				},
				'Surrey - Central': {
					sales: 29,
					newListings: 90,
					activeListings: 230,
					benchmarkPrice: 557500,
					medianPrice: 505000,
					averagePrice: 512582
				},
				Cloverdale: {
					sales: 16,
					newListings: 38,
					activeListings: 86,
					benchmarkPrice: 586100,
					medianPrice: 582500,
					averagePrice: 592118
				},
				'North Surrey': {
					sales: 95,
					newListings: 352,
					activeListings: 861,
					benchmarkPrice: 474500,
					medianPrice: 465000,
					averagePrice: 487792
				}
			}
		},
		'May 2025': {
			detached: {
				Abbotsford: {
					sales: 104,
					newListings: 250,
					activeListings: 526,
					benchmarkPrice: 1202300,
					averagePrice: 1232279
				},
				Mission: {
					sales: 37,
					newListings: 96,
					activeListings: 249,
					benchmarkPrice: 1032600,
					averagePrice: 1030286
				},
				'South Surrey White Rock': {
					sales: 77,
					newListings: 163,
					activeListings: 454,
					benchmarkPrice: 1844400,
					averagePrice: 2117166
				},
				Langley: {
					sales: 57,
					newListings: 194,
					activeListings: 400,
					benchmarkPrice: 1617700,
					averagePrice: 1615749
				},
				'N. Delta': {
					sales: 16,
					newListings: 85,
					activeListings: 181,
					benchmarkPrice: 1398800,
					averagePrice: 1475437
				},
				'City of Surrey - Combined*': {
					sales: 124,
					benchmarkPrice: 1622200,
					averagePrice: 1698154
				},
				Surrey: {
					sales: 46,
					newListings: 213,
					activeListings: 598,
					benchmarkPrice: 1520800,
					averagePrice: 1610978
				},
				Cloverdale: {
					sales: 28,
					newListings: 75,
					activeListings: 150,
					benchmarkPrice: 1529200,
					averagePrice: 1439764
				},
				'North Surrey': {
					sales: 18,
					newListings: 134,
					activeListings: 369,
					benchmarkPrice: 1524700,
					averagePrice: 1565944
				}
			},
			townhouse: {
				Abbotsford: {
					sales: 73,
					newListings: 135,
					activeListings: 210,
					benchmarkPrice: 695100,
					averagePrice: 688963
				},
				Mission: {
					sales: 13,
					newListings: 30,
					activeListings: 52,
					benchmarkPrice: 668700,
					averagePrice: 664538
				},
				'South Surrey White Rock': {
					sales: 43,
					newListings: 90,
					activeListings: 187,
					benchmarkPrice: 1012200,
					averagePrice: 1008189
				},
				Langley: {
					sales: 83,
					newListings: 184,
					activeListings: 262,
					benchmarkPrice: 883600,
					averagePrice: 877668
				},
				'N. Delta': {
					sales: 11,
					newListings: 26,
					activeListings: 38,
					benchmarkPrice: 884900,
					averagePrice: 908981
				},
				'City of Surrey - Combined*': {
					sales: 151,
					benchmarkPrice: 890700,
					averagePrice: 878414
				},
				Surrey: {
					sales: 45,
					newListings: 107,
					activeListings: 221,
					benchmarkPrice: 833900,
					averagePrice: 808154
				},
				Cloverdale: {
					sales: 37,
					newListings: 68,
					activeListings: 111,
					benchmarkPrice: 880500,
					averagePrice: 886302
				},
				'North Surrey': {
					sales: 15,
					newListings: 48,
					activeListings: 97,
					benchmarkPrice: 824800,
					averagePrice: 812860
				}
			},
			apartment: {
				Abbotsford: {
					sales: 68,
					newListings: 165,
					activeListings: 358,
					benchmarkPrice: 541700,
					averagePrice: 525957
				},
				Mission: {
					sales: 11,
					newListings: 28,
					activeListings: 67,
					benchmarkPrice: 470200,
					averagePrice: 472354
				},
				'South Surrey White Rock': {
					sales: 53,
					newListings: 136,
					activeListings: 346,
					benchmarkPrice: 674000,
					averagePrice: 674582
				},
				Langley: {
					sales: 94,
					newListings: 226,
					activeListings: 425,
					benchmarkPrice: 637400,
					averagePrice: 607987
				},
				'N. Delta': {
					sales: 19,
					newListings: 34,
					activeListings: 79,
					benchmarkPrice: 574300,
					averagePrice: 560942
				},
				'City of Surrey - Combined*': {
					sales: 202,
					benchmarkPrice: 604500,
					averagePrice: 603230
				},
				Surrey: {
					sales: 81,
					newListings: 235,
					activeListings: 612,
					benchmarkPrice: 559500,
					averagePrice: 557280
				},
				Cloverdale: {
					sales: 15,
					newListings: 38,
					activeListings: 62,
					benchmarkPrice: 648300,
					averagePrice: 659426
				},
				'North Surrey': {
					sales: 44,
					newListings: 136,
					activeListings: 349,
					benchmarkPrice: 538000,
					averagePrice: 524475
				}
			}
		},
		'June 2025': {
			detached: {
				Abbotsford: {
					sales: 135,
					newListings: 290,
					activeListings: 659,
					benchmarkPrice: 1219600,
					medianPrice: 1200000,
					averagePrice: 1263331
				},
				Mission: {
					sales: 45,
					newListings: 110,
					activeListings: 279,
					benchmarkPrice: 1029900,
					medianPrice: 1030000,
					averagePrice: 1059219
				},
				'South Surrey White Rock': {
					sales: 89,
					newListings: 260,
					activeListings: 645,
					benchmarkPrice: 1922600,
					medianPrice: 1800000,
					averagePrice: 2058111
				},
				Langley: {
					sales: 106,
					newListings: 285,
					activeListings: 574,
					benchmarkPrice: 1617100,
					medianPrice: 1577500,
					averagePrice: 1659680
				},
				'N. Delta': {
					sales: 48,
					newListings: 119,
					activeListings: 228,
					benchmarkPrice: 1460900,
					medianPrice: 1500000,
					averagePrice: 1509833
				},
				Surrey: {
					sales: 275,
					newListings: 741,
					activeListings: 1440,
					benchmarkPrice: 1607400,
					medianPrice: 1580000,
					averagePrice: 1673737
				},
				'Surrey - Central': {
					sales: 109,
					newListings: 295,
					activeListings: 579,
					benchmarkPrice: 1563000,
					medianPrice: 1570000,
					averagePrice: 1630950
				},
				Cloverdale: {
					sales: 77,
					newListings: 186,
					activeListings: 341,
					benchmarkPrice: 1570300,
					medianPrice: 1550000,
					averagePrice: 1623391
				},
				'North Surrey': {
					sales: 89,
					newListings: 260,
					activeListings: 520,
					benchmarkPrice: 1587300,
					medianPrice: 1599000,
					averagePrice: 1732940
				}
			},
			townhouse: {
				Abbotsford: {
					sales: 87,
					newListings: 187,
					activeListings: 297,
					benchmarkPrice: 719900,
					medianPrice: 705000,
					averagePrice: 725188
				},
				Mission: {
					sales: 17,
					newListings: 40,
					activeListings: 67,
					benchmarkPrice: 687000,
					medianPrice: 665000,
					averagePrice: 698082
				},
				'South Surrey White Rock': {
					sales: 54,
					newListings: 135,
					activeListings: 231,
					benchmarkPrice: 1006200,
					medianPrice: 962500,
					averagePrice: 1009484
				},
				Langley: {
					sales: 111,
					newListings: 279,
					activeListings: 406,
					benchmarkPrice: 917200,
					medianPrice: 888000,
					averagePrice: 917610
				},
				'N. Delta': {
					sales: 21,
					newListings: 44,
					activeListings: 74,
					benchmarkPrice: 923900,
					medianPrice: 920000,
					averagePrice: 914924
				},
				'City of Surrey - Combined*': {
					sales: 213,
					newListings: 512,
					activeListings: 778,
					benchmarkPrice: 916700,
					medianPrice: 890000,
					averagePrice: 911211
				},
				'Surrey - Central': {
					sales: 86,
					newListings: 202,
					activeListings: 300,
					benchmarkPrice: 861100,
					medianPrice: 862000,
					averagePrice: 868761
				},
				Cloverdale: {
					sales: 63,
					newListings: 153,
					activeListings: 235,
					benchmarkPrice: 973800,
					medianPrice: 950000,
					averagePrice: 975190
				},
				'North Surrey': {
					sales: 64,
					newListings: 157,
					activeListings: 243,
					benchmarkPrice: 856200,
					medianPrice: 838000,
					averagePrice: 860195
				}
			},
			apartment: {
				Abbotsford: {
					sales: 94,
					newListings: 216,
					activeListings: 421,
					benchmarkPrice: 541600,
					medianPrice: 517500,
					averagePrice: 526488
				},
				Mission: {
					sales: 17,
					newListings: 36,
					activeListings: 86,
					benchmarkPrice: 521600,
					medianPrice: 520000,
					averagePrice: 503641
				},
				'South Surrey White Rock': {
					sales: 68,
					newListings: 191,
					activeListings: 468,
					benchmarkPrice: 665600,
					medianPrice: 642450,
					averagePrice: 698046
				},
				Langley: {
					sales: 130,
					newListings: 309,
					activeListings: 569,
					benchmarkPrice: 638400,
					medianPrice: 630000,
					averagePrice: 639521
				},
				'N. Delta': {
					sales: 22,
					newListings: 46,
					activeListings: 88,
					benchmarkPrice: 564200,
					medianPrice: 562500,
					averagePrice: 556127
				},
				'City of Surrey - Combined*': {
					sales: 236,
					newListings: 651,
					activeListings: 1323,
					benchmarkPrice: 566400,
					medianPrice: 550000,
					averagePrice: 577094
				},
				'Surrey - Central': {
					sales: 147,
					newListings: 388,
					activeListings: 777,
					benchmarkPrice: 536200,
					medianPrice: 530000,
					averagePrice: 543622
				},
				Cloverdale: {
					sales: 28,
					newListings: 93,
					activeListings: 167,
					benchmarkPrice: 659100,
					medianPrice: 649450,
					averagePrice: 674834
				},
				'North Surrey': {
					sales: 61,
					newListings: 170,
					activeListings: 379,
					benchmarkPrice: 531000,
					medianPrice: 520000,
					averagePrice: 545066
				}
			}
		},
		'July 2025': {
			detached: {
				Abbotsford: {
					sales: 64,
					newListings: 178,
					activeListings: 512,
					benchmarkPrice: 1180200,
					medianPrice: 1154600,
					averagePrice: 1204726
				},
				Mission: {
					sales: 36,
					newListings: 89,
					activeListings: 303,
					benchmarkPrice: 1033100,
					medianPrice: 955000,
					averagePrice: 959916
				},
				'South Surrey White Rock': {
					sales: 54,
					newListings: 210,
					activeListings: 661,
					benchmarkPrice: 1837300,
					medianPrice: 1658000,
					averagePrice: 1985981
				},
				Langley: {
					sales: 89,
					newListings: 216,
					activeListings: 571,
					benchmarkPrice: 1598600,
					medianPrice: 1400000,
					averagePrice: 1564635
				},
				'N. Delta': {
					sales: 31,
					newListings: 103,
					activeListings: 291,
					benchmarkPrice: 1333600,
					medianPrice: 1391500,
					averagePrice: 1423387
				},
				'City of Surrey - Combined*': {
					sales: 167,
					benchmarkPrice: 1561700,
					averagePrice: 1600371
				},
				'Surrey - Central': {
					sales: 55,
					newListings: 248,
					activeListings: 835,
					benchmarkPrice: 1468200,
					medianPrice: 1485000,
					averagePrice: 1547333
				},
				Cloverdale: {
					sales: 31,
					newListings: 95,
					activeListings: 220,
					benchmarkPrice: 1435500,
					medianPrice: 1380000,
					averagePrice: 1418412
				},
				'North Surrey': {
					sales: 37,
					newListings: 137,
					activeListings: 474,
					benchmarkPrice: 1435300,
					medianPrice: 1420000,
					averagePrice: 1433535
				}
			},
			townhouse: {
				Abbotsford: {
					sales: 32,
					newListings: 88,
					activeListings: 242,
					benchmarkPrice: 653400,
					medianPrice: 651500,
					averagePrice: 645918
				},
				Mission: {
					sales: 8,
					newListings: 21,
					activeListings: 47,
					benchmarkPrice: 663000,
					medianPrice: 644500,
					averagePrice: 618112
				},
				'South Surrey White Rock': {
					sales: 41,
					newListings: 136,
					activeListings: 287,
					benchmarkPrice: 928200,
					medianPrice: 1078000,
					averagePrice: 1083484
				},
				Langley: {
					sales: 82,
					newListings: 147,
					activeListings: 328,
					benchmarkPrice: 852300,
					medianPrice: 820000,
					averagePrice: 832423
				},
				'N. Delta': {
					sales: 10,
					newListings: 18,
					activeListings: 38,
					benchmarkPrice: 932900,
					medianPrice: 855000,
					averagePrice: 894598
				},
				'City of Surrey - Combined*': {
					sales: 167,
					benchmarkPrice: 838700,
					averagePrice: 842326
				},
				'Surrey - Central': {
					sales: 73,
					newListings: 224,
					activeListings: 547,
					benchmarkPrice: 809300,
					medianPrice: 795000,
					averagePrice: 754317
				},
				Cloverdale: {
					sales: 32,
					newListings: 78,
					activeListings: 165,
					benchmarkPrice: 821500,
					medianPrice: 809900,
					averagePrice: 820714
				},
				'North Surrey': {
					sales: 21,
					newListings: 57,
					activeListings: 146,
					benchmarkPrice: 750800,
					medianPrice: 650000,
					averagePrice: 710366
				}
			},
			apartment: {
				Abbotsford: {
					sales: 58,
					newListings: 163,
					activeListings: 383,
					benchmarkPrice: 424400,
					medianPrice: 439500,
					averagePrice: 472611
				},
				Mission: {
					sales: 11,
					newListings: 15,
					activeListings: 44,
					benchmarkPrice: 441500,
					medianPrice: 400000,
					averagePrice: 434790
				},
				'South Surrey White Rock': {
					sales: 60,
					newListings: 119,
					activeListings: 379,
					benchmarkPrice: 584400,
					medianPrice: 635000,
					averagePrice: 752684
				},
				Langley: {
					sales: 82,
					newListings: 220,
					activeListings: 524,
					benchmarkPrice: 590900,
					medianPrice: 550000,
					averagePrice: 558508
				},
				'N. Delta': {
					sales: 11,
					newListings: 23,
					activeListings: 57,
					benchmarkPrice: 555800,
					medianPrice: 556097,
					averagePrice: 558603
				},
				'City of Surrey - Combined*': {
					sales: 161,
					benchmarkPrice: 511600,
					averagePrice: 529350
				},
				'Surrey - Central': {
					sales: 19,
					newListings: 89,
					activeListings: 248,
					benchmarkPrice: 547400,
					medianPrice: 480000,
					averagePrice: 486784
				},
				Cloverdale: {
					sales: 22,
					newListings: 32,
					activeListings: 83,
					benchmarkPrice: 580700,
					medianPrice: 554500,
					averagePrice: 550427
				},
				'North Surrey': {
					sales: 93,
					newListings: 277,
					activeListings: 873,
					benchmarkPrice: 458200,
					medianPrice: 495000,
					averagePrice: 509949
				}
			}
		}
	};
	const mergedMarketData = {
		...marketDataFVRBpercityAllMonths,
		...marketDataGVRpercityAllMonths
	};
	if (!listing || !listing.City || !listing.Area) {
		return null;
	}
	const date = new Date();
	date.setMonth(date.getMonth() - 1); // Set to last month
	const lastMonth = date.toLocaleString('default', { month: 'long', year: 'numeric' });

	// Get formatted month string for a specific number of months ago
	const getMonthString = (monthsAgo) => {
		const dateObj = new Date();
		dateObj.setMonth(dateObj.getMonth() - monthsAgo);
		return dateObj.toLocaleString('default', { month: 'long', year: 'numeric' });
	};

	// Try to get data for last month, fallback to previous months if needed
	const getMarketData = (dataObject) => {
		// Try last month (1 month ago)
		if (dataObject[getMonthString(1)]) {
			return dataObject[getMonthString(1)];
		}
		// Try 2 months ago
		else if (dataObject[getMonthString(2)]) {
			return dataObject[getMonthString(2)];
		}
		return {};
	};

	// Get market data using fallback logic
	const marketDataFVRBpercity = getMarketData(marketDataFVRBpercityAllMonths);
	const marketDataGVRpercity = getMarketData(marketDataGVRpercityAllMonths);
	// Get the month to display in the UI for GVR
	let displayMonth = lastMonth;
	if (mergedMarketData[getMonthString(1)]) {
		displayMonth = getMonthString(1);
	} else if (mergedMarketData[getMonthString(2)]) {
		displayMonth = getMonthString(2);
	}

	const getPropertyTypeKey = (dwellingClassification, type) => {
		dwellingClassification = dwellingClassification?.toLowerCase();
		type = type?.toLowerCase();
		if (dwellingClassification === 'detached') return 'detached';
		if (
			dwellingClassification === 'attached' &&
			['1/2 duplex', 'duplex', 'triplex', 'townhouse'].includes(type)
		)
			return 'attached';
		if (
			dwellingClassification === 'attached' &&
			['apartment/', 'apartment/condo', 'apartment complex (10+ units)'].includes(type)
		)
			return 'apartment';
		return null;
	};
	const propertyTypeKey = getPropertyTypeKey(listing.DwellingClassification, listing.Type);
	console.log('propertyTypeKey', propertyTypeKey);
	if (
		propertyTypeKey === null
		//||
		// !marketDataFVRBpercity ||
		// !marketDataGVRpercity ||
		// !marketDataGVRpercity[propertyTypeKey]
	) {
		console.log('return is as null');
		return null;
	}
	console.log('here');
	// Use Area as fallback if City is not available
	let locationKey = listing.Area;
	let cityOrAreaBanner = listing.Area;

	// Function to find data by partial key match
	function findDataByLocationKey(marketData, propertyTypeKey, locationKey, city) {
		let data = marketData[propertyTypeKey];
		if (!data) return null;

		// Convert keys to lowercase for case-insensitive comparison
		const lowerCaseLocationKey = locationKey.toLowerCase();
		const lowerCaseCity = city.toLowerCase();

		// Check for exact match with locationKey
		const exactMatchKey = Object.keys(data).find(
			(key) => key.toLowerCase() === lowerCaseLocationKey
		);
		if (exactMatchKey) {
			return data[exactMatchKey];
		}

		// Check for exact match with city
		const exactMatchCity = Object.keys(data).find((key) => key.toLowerCase() === lowerCaseCity);
		if (exactMatchCity) {
			return data[exactMatchCity];
		}
		console.log('exactMatchCity', exactMatchCity);
		// Find a key that includes the locationKey
		const matchingKey = Object.keys(data).find((key) =>
			key.toLowerCase().includes(lowerCaseLocationKey)
		);
		if (matchingKey) {
			data = data[matchingKey];
			data.matchingKey = matchingKey;
			return data;
		}
		return null;
	}

	// Attempt to get data from both sources
	let data =
		findDataByLocationKey(marketDataGVRpercity, propertyTypeKey, locationKey, listing.City) ||
		findDataByLocationKey(marketDataFVRBpercity, propertyTypeKey, locationKey, listing.City);
	console.log('data', data);
	// Ensure data is initialized
	data = data || {};

	// Safely access the benchmark price with optional chaining, using fallback months if needed
	let benchmarkPrice = null;

	// Try last month first
	benchmarkPrice =
		benchmarkPriceGVRpercityAllMonths?.[getMonthString(1)]?.[propertyTypeKey]?.[locationKey]
			?.benchmarkPrice ||
		benchmarkPriceGVRpercityAllMonths?.[getMonthString(1)]?.[propertyTypeKey]?.[listing.Area]
			?.benchmarkPrice;

	// Try 2 months ago if last month is not available
	if (!benchmarkPrice) {
		benchmarkPrice =
			benchmarkPriceGVRpercityAllMonths?.[getMonthString(2)]?.[propertyTypeKey]?.[locationKey]
				?.benchmarkPrice ||
			benchmarkPriceGVRpercityAllMonths?.[getMonthString(2)]?.[propertyTypeKey]?.[
				listing.Area
			]?.benchmarkPrice;
	}

	// Try 3 months ago if 2 months ago is not available
	if (!benchmarkPrice) {
		benchmarkPrice =
			benchmarkPriceGVRpercityAllMonths?.[getMonthString(3)]?.[propertyTypeKey]?.[locationKey]
				?.benchmarkPrice ||
			benchmarkPriceGVRpercityAllMonths?.[getMonthString(3)]?.[propertyTypeKey]?.[
				listing.Area
			]?.benchmarkPrice;
	}

	// Assign the benchmark price to data
	if (data) {
		data.benchmarkPrice = benchmarkPrice;
	}

	if (!data) {
		return null;
	}

	const getPropertyIcon = (propertyType) => {
		if (propertyType === 'detached') return <HomeIcon className={classes.icon} />;
		if (propertyType === 'attached') return <BusinessIcon className={classes.icon} />;
		if (propertyType === 'apartment') return <ApartmentIcon className={classes.icon} />;
		return null;
	};

	function calculateSalesRatio(sales, activeListings) {
		if (activeListings === 0) {
			return 0; // Avoid division by zero
		}

		const ratio = (100 * sales) / activeListings;
		return parseFloat(ratio.toFixed(1));
	}

	return (
		<Paper className={classes.paper} elevation={3}>
			<Box className={classes.root}>
				<Typography variant="h5" className={classes.title}>
					{data.matchingKey
						? data.matchingKey.toUpperCase()
						: cityOrAreaBanner.toUpperCase()}{' '}
					MARKET STATISTICS
				</Typography>
				<Typography variant="subtitle1" className={classes.subtitle}>
					{displayMonth.toUpperCase()}
				</Typography>

				<Box className={classes.propertyTypeSection}>
					{getPropertyIcon(propertyTypeKey)}
					<Typography variant="h6" className={classes.propertyTypeTitle}>
						{propertyTypeKey.toUpperCase()}
					</Typography>

					<Grid container spacing={3}>
						{data.benchmarkPrice && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										Benchmark Price
									</Typography>
									<Typography className={classes.statValue}>
										{currencyParser(data.benchmarkPrice)}
									</Typography>
								</Box>
							</Grid>
						)}
						{data.sales && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										Total Sales
									</Typography>
									<Typography className={classes.statValue}>
										{data.sales.toLocaleString()}
									</Typography>
								</Box>
							</Grid>
						)}
						{data.listings && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										Active Listings
									</Typography>
									<Typography className={classes.statValue}>
										{data.listings.toLocaleString()}
									</Typography>
								</Box>
							</Grid>
						)}
						{data.newListings && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										New Listings
									</Typography>
									<Typography className={classes.statValue}>
										{data.newListings.toLocaleString()}
									</Typography>
								</Box>
							</Grid>
						)}
						{data.medianPrice && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										Median Price
									</Typography>
									<Typography className={classes.statValue}>
										{currencyParser(data.medianPrice)}
									</Typography>
								</Box>
							</Grid>
						)}
						{data.averagePrice && (
							<Grid item xs={12} sm={4}>
								<Box className={classes.statItem}>
									<Typography className={classes.statLabel}>
										Average Price
									</Typography>
									<Typography className={classes.statValue}>
										{currencyParser(data.averagePrice)}
									</Typography>
								</Box>
							</Grid>
						)}
					</Grid>
				</Box>

				<Box className={classes.cityStats}>
					<Typography variant="h6" align="center" gutterBottom>
						Sales-to-Listings Ratio
					</Typography>

					{data.sales && (data.listings || data.activeListings) ? (
						<>
							<SalesRatioProgressBar
								salesRatio={calculateSalesRatio(
									data.sales,
									data.listings ?? data.activeListings
								)}
								propertyType={propertyTypeKey.toUpperCase() || 'PROPERTY'}
							/>

							<Divider style={{ margin: '16px 0' }} />
						</>
					) : (
						<>
							<Typography className={classes.noDataMessage}>
								Data not available
							</Typography>

							<Divider style={{ margin: '16px 0' }} />
						</>
					)}
				</Box>
				{/* 
				<Typography
					variant="body2"
					align="center"
					style={{ marginTop: '16px', color: '#666' }}
				>
					Data source: Real Estate Board of Greater Vancouver
				</Typography> */}
			</Box>
		</Paper>
	);
}
